import { ConditionnementDoc } from './ConditionnementDoc';
import { useEffect, useMemo, useState } from 'react';
import { X, PackagePlus } from '../../lib/icons';
import { toast } from 'sonner';
import {
  fetchFormats,
  enregistrerConditionnement,
  litresVersKg,
  DENSITE_HUILE,
  DENOMINATIONS_HUILE,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

export const ConditionnementDialog = ({ cuves, cuveInitiale = '', onClose, onDone }) => {
  const [fiche, setFiche] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [formats, setFormats] = useState([]);
  const [cuveId, setCuveId] = useState(cuveInitiale);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [qtes, setQtes] = useState({});
  const [denomination, setDenomination] = useState(DENOMINATIONS_HUILE[0]);

  useEffect(() => {
    fetchFormats().then(setFormats);
  }, []);

  const cuve = cuves.find((c) => c.id === cuveId);
  const lignes = Object.entries(qtes)
    .filter(([, u]) => Number(u) > 0)
    .map(([formatId, unites]) => ({ formatId, unites: Number(unites) }));

  const calc = useMemo(() => {
    const litres = lignes.reduce((s, l) => s + l.unites * (formats.find((f) => f.id === l.formatId)?.litres ?? 0), 0);
    return { litres: Number(litres.toFixed(3)), kg: litresVersKg(litres) };
  }, [lignes, formats]);

  const submit = async (e) => {
    e.preventDefault();
    if (enCours) return;
    setEnCours(true);
    try {
    const { error, row } = await enregistrerConditionnement({ date, cuveId, denomination, lignes });
    if (error) return toast.error(error);
    toast.success(`Conditionnement ${row.id} — ${fmt(row.kg, 3)} kg soutirés de ${row.cuveNom}`);
    onDone?.(row);
    setFiche({...row, confirme:true});
    } catch (e) { toast.error(e.message || 'Conditionnement non confirmé : vérifier le registre avant de réessayer.'); }
    finally { setEnCours(false); }
  };

  return (
    <>
    {fiche && <ConditionnementDoc row={fiche} projet={!fiche.confirme} onClose={() => fiche.confirme ? onClose() : setFiche(null)} />}
    {!fiche && <div data-testid="conditionnement-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={submit} data-testid="conditionnement-form" className="max-h-[92vh] w-full max-w-2xl overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <PackagePlus size={18} /> Mise en bouteille / Conditionnement
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Cuve → emballages vides consommés → produits finis (SKU)
            </p>
          </div>
          <button type="button" data-testid="conditionnement-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
          <div>
            <label className={label}>Date</label>
            <input type="date" data-testid="cond-date" value={date} onChange={(e) => setDate(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>Cuve de départ</label>
            <select data-testid="cond-cuve" value={cuveId} onChange={(e) => setCuveId(e.target.value)} className={field}>
              <option value="">— Choisir —</option>
              {cuves.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom} · {c.famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · {fmt(c.volume)} kg
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={label}>Qualité / dénomination de l'huile</label>
            <select data-testid="cond-denomination" value={denomination} onChange={(e) => setDenomination(e.target.value)} className={field}>
              {DENOMINATIONS_HUILE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="px-5 pb-5">
          <p className={label}>Emballages vides à utiliser</p>
          <div className="mt-2 divide-y divide-slate-100 border border-slate-200">
            {formats.map((f) => (
              <div key={f.id} data-testid={`cond-format-${f.id}`} className="flex items-center gap-3 px-3 py-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{f.nom}</p>
                  <p className="font-data text-[11px] text-slate-500">
                    {f.litres} L · emballages vides disponibles {fmt(f.stock)}
                  </p>
                </div>
                <input
                  data-testid={`cond-unites-${f.id}`}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={qtes[f.id] ?? ''}
                  onChange={(e) => setQtes((p) => ({ ...p, [f.id]: e.target.value }))}
                  className="h-11 w-24 border border-slate-300 bg-slate-50 px-2 text-center font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
              </div>
            ))}
          </div>
        </div>

        <div data-testid="cond-simulation" className="grid grid-cols-1 gap-px border-y border-slate-200 bg-slate-200 sm:grid-cols-4">
          {[
            ['Volume conditionné', `${fmt(calc.litres, 3)} L`],
            ['Huile soutirée', `${fmt(calc.kg, 3)} kg`],
            ['Reste en cuve', cuve ? `${fmt(cuve.volume - calc.kg, 3)} kg` : '—'],
            ['Produits finis créés', `${fmt(lignes.reduce((s, l) => s + l.unites, 0))} u. — ${denomination}`],
          ].map(([k, v]) => (
            <div key={k} className="bg-white px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.08em] text-slate-500">{k}</p>
              <p className="tabular font-data text-sm font-semibold text-slate-900">{v}</p>
            </div>
          ))}
        </div>
        <p className="px-5 py-2 font-data text-[11px] text-slate-500">Conversion : 1 L = {DENSITE_HUILE} kg</p>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button type="button" data-testid="cond-fiche-preparation" disabled={enCours || !cuve || !lignes.length} onClick={() => setFiche({date, cuveNom:cuve.nom, denomination, lignes:lignes.map(l => ({...l, nom:formats.find(f=>f.id===l.formatId).nom, litres:formats.find(f=>f.id===l.formatId).litres})), unites:lignes.reduce((n,l)=>n+l.unites,0), ...calc})} className="h-11 border border-slate-300 px-3 text-sm disabled:opacity-50">Fiche à imprimer</button>
          <button type="submit" data-testid="cond-submit" disabled={enCours || lignes.length === 0 || !cuveId} className="h-11 flex-1 disabled:bg-slate-300 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700">
            Valider le conditionnement
          </button>
          <button type="button" data-testid="cond-cancel" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            Annuler
          </button>
        </div>
      </form>
    </div>}
    </>
  );
};
